import React, { useCallback, useEffect, useRef, useState } from 'react'

interface Point {
  x: number
  y: number
}

export interface Size {
  width: number
  height: number
}

interface Gesture {
  start: Point
  startOffset: Point
  startScale: number
  startDistance: number
  moved: boolean
}

interface Options {
  /** On-screen size of the image at "fit to screen" (scale 1), or null while unknown. */
  fitted: Size | null
  /** Size of the image area (between the top and bottom control bars). */
  stage: Size
  onSwipe: (direction: 1 | -1) => void
}

export const MIN_SCALE = 1
export const MAX_SCALE = 3
const STEP = 1.5
const WHEEL_STEP = 1.12
const DOUBLE_TAP_SCALE = 2
const DOUBLE_TAP_MS = 300
const SWIPE_PX = 60
const ORIGIN: Point = { x: 0, y: 0 }

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y)

/**
 * Single zoom system for the viewer. Scale 1 is the fitted size; zoom is
 * limited to 1×–3× and panning is clamped so the image never leaves the
 * image area. Any change of image or stage size resets to fit.
 */
export function useZoomPan({ fitted, stage, onSwipe }: Options) {
  const [scale, setScale] = useState(MIN_SCALE)
  const [offset, setOffset] = useState<Point>(ORIGIN)
  const [interacting, setInteracting] = useState(false)
  const scaleRef = useRef(MIN_SCALE)
  const offsetRef = useRef<Point>(ORIGIN)
  const bounds = useRef({ fitted, stage })
  bounds.current = { fitted, stage }
  const pointers = useRef(new Map<number, Point>())
  const gesture = useRef<Gesture | null>(null)
  const lastTap = useRef(0)

  const clampOffset = useCallback((next: Point, s: number): Point => {
    const { fitted: f, stage: st } = bounds.current
    if (!f) return ORIGIN
    const maxX = Math.max(0, (f.width * s - st.width) / 2)
    const maxY = Math.max(0, (f.height * s - st.height) / 2)
    return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) }
  }, [])

  const commit = useCallback(
    (s: number, o: Point) => {
      let nextScale = clamp(s, MIN_SCALE, MAX_SCALE)
      if (nextScale < 1.01) nextScale = MIN_SCALE
      const nextOffset = nextScale === MIN_SCALE ? ORIGIN : clampOffset(o, nextScale)
      scaleRef.current = nextScale
      offsetRef.current = nextOffset
      setScale(nextScale)
      setOffset(nextOffset)
    },
    [clampOffset],
  )

  /** Zoom around the centre of the image area, keeping the visible point steady. */
  const zoomTo = useCallback(
    (target: number) => {
      const prev = scaleRef.current
      const next = clamp(target, MIN_SCALE, MAX_SCALE)
      const ratio = next / prev
      commit(next, { x: offsetRef.current.x * ratio, y: offsetRef.current.y * ratio })
    },
    [commit],
  )

  const reset = useCallback(() => {
    pointers.current.clear()
    gesture.current = null
    setInteracting(false)
    commit(MIN_SCALE, ORIGIN)
  }, [commit])
  const zoomIn = useCallback(() => zoomTo(scaleRef.current * STEP), [zoomTo])
  const zoomOut = useCallback(() => zoomTo(scaleRef.current / STEP), [zoomTo])

  // New image, rotation or window resize → back to "fit to screen".
  useEffect(() => {
    reset()
  }, [reset, fitted?.width, fitted?.height, stage.width, stage.height])

  const startGesture = (start: Point, moved: boolean) => {
    const pts = Array.from(pointers.current.values())
    gesture.current = {
      start,
      startOffset: offsetRef.current,
      startScale: scaleRef.current,
      startDistance: pts.length >= 2 ? distance(pts[0], pts[1]) : 0,
      moved,
    }
  }

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    setInteracting(true)
    startGesture({ x: event.clientX, y: event.clientY }, pointers.current.size > 1)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const g = gesture.current
    if (!g) return

    const pts = Array.from(pointers.current.values())
    if (pts.length >= 2 && g.startDistance > 0) {
      g.moved = true
      const next = clamp((g.startScale * distance(pts[0], pts[1])) / g.startDistance, MIN_SCALE, MAX_SCALE)
      const ratio = next / g.startScale
      commit(next, { x: g.startOffset.x * ratio, y: g.startOffset.y * ratio })
      return
    }

    const dx = event.clientX - g.start.x
    const dy = event.clientY - g.start.y
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) g.moved = true
    // Drag only while zoomed in.
    if (scaleRef.current > MIN_SCALE) {
      commit(scaleRef.current, { x: g.startOffset.x + dx, y: g.startOffset.y + dy })
    }
  }

  const onPointerEnd = (event: React.PointerEvent<HTMLElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    const g = gesture.current
    const wasLast = pointers.current.size === 1
    pointers.current.delete(event.pointerId)

    if (!wasLast) {
      const [rest] = Array.from(pointers.current.values())
      startGesture(rest, true)
      return
    }

    setInteracting(false)
    gesture.current = null
    if (!g) return

    const dx = event.clientX - g.start.x
    const dy = event.clientY - g.start.y

    if (!g.moved) {
      const now = performance.now()
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0
        if (scaleRef.current > MIN_SCALE) commit(MIN_SCALE, ORIGIN)
        else zoomTo(DOUBLE_TAP_SCALE)
      } else {
        lastTap.current = now
      }
      return
    }

    if (
      g.startScale === MIN_SCALE &&
      scaleRef.current === MIN_SCALE &&
      Math.abs(dx) > SWIPE_PX &&
      Math.abs(dx) > Math.abs(dy) * 1.2
    ) {
      onSwipe(dx < 0 ? 1 : -1)
    }
  }

  const onWheel = (event: React.WheelEvent<HTMLElement>) => {
    zoomTo(scaleRef.current * (event.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP))
  }

  return {
    scale,
    offset,
    interacting,
    zoomIn,
    zoomOut,
    reset,
    canZoomIn: scale < MAX_SCALE - 0.001,
    canZoomOut: scale > MIN_SCALE,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      onLostPointerCapture: onPointerEnd,
      onWheel,
    },
  }
}
